/**
 * 次数无限制
 */
export const COUNT_NO_LIMIT = -101;

/**
 * 用户状态(二进制状态码)
 */
export enum UserStatus {
  INACTIVE = 0,
  WECHAT = 1, //已绑定微信
  PASSWORD = 1 << 1, //已设置密码
  PHONE = 1 << 2, //已设置手机号码
}

/**
 * 用户对应的微信信息
 */
export type IWechatInfo = {
  /**
   * 微信信息id
   */
  id: number;
  /**
   * 微信头像
   */
  headimgurl?: string;
  /**
   * 微信昵称
   */
  nickname?: string;
};

/**
 * 用户钱包信息
 */
export type IUserWallet = {
  /**
   * 钱包信息id
   */
  id: number;
  /**
   * 所属用户id
   */
  user_id: number;
  /**
   * 钱包总额
   */
  total_amount: number;
  /**
   * 已提现金额
   */
  withdrawed: number;
  /**
   * 钱包信息更新时间
   */
  updated_at: number;
  /**
   * 钱包信息创建时间
   */
  created_at: number;
  /**
   * 将到账的金额，单位分
   */
  coming_amount: number;
};

/**
 * 基础用户信息
 */
export type IBaseUser = {
  /**
   * 用户id
   */
  id: number;
  /**
   * 用户手机号码
   */
  phone: string;
  /**
   * 用户状态
   */
  status: UserStatus;
  /**
   * 用户昵称(默认为微信昵称)
   */
  nickname?: string;
  /**
   * 纠错试用次数
   */
  correct_test_count: number;
  /**
   * 智能创作试用次数
   */
  ai_copywriting_test_count: number;
};

/**
 * 用户信息，新服务端api接口返回的用户信息，
 * 一般为`userInfo`字段对应的值，旧字段`userInfo`返回
 * 的值则对应{@link IUser}
 */
export type IUserInfo = IBaseUser & {
  /**
   * 微信信息id
   */
  wx_user_id?: number;
  /**
   * 邀请人用户id
   */
  invitor_id?: number;
  /**
   * API接口访问Token
   */
  access_token: string;
  /**
   * 分销收益（邀请的用户购买支付套餐时当前用户得到的收益百分比）
   */
  profit_percent: number;
  /**
   * 剩余纠错或智能创作次数，值为{@link COUNT_NO_LIMIT}时表示无限
   */
  correct_count: number;
  /**
   * 已邀请朋友数
   */
  invitees_count: number;
  /**
   * 用户微信信息
   */
  wechatInfo?: IWechatInfo;
  /**
   * 用户钱包信息
   */
  wallet: IUserWallet;
};

/**
 * 用户信息，旧服务端api接口返回的用户信息，
 * 一般为`user`字段对应的值，新字段`userInfo`返回
 * 的值则对应{@link IUserInfo}
 */
export type IUser = IBaseUser & {
  /**
   * 用户邀请码
   */
  inviteCode: string;
  /**
   * 用户填写的邀请码
   */
  invitedCode?: string;
  /**
   * 访问Token
   */
  accessToken: string;
  /**
   * 剩余纠错次数，-101表示无限
   */
  correctCount: number;
  /**
   * 用户头像，最后一个数值代表正方形头像
   * 大小（有 0、46、64、96、132 数值可选，0 代表 640*640 正方形头像），用
   * 户没有头像时该项为空，默认为132
   * 如: https://thirdwx.qlogo.cn/mmopen/vi_32/Q0j4TwGTfTJBldWDdFMnRX0yXMdBSPeThAuTrTZwoogs77AGu4HX9iaiaqzn3bQWHuuw28elL0PGqpricEFeHibsAw/132
   */
  avatar?: string;
  /**
   * 用户微信sessionKey
   */
  sessionKey?: string;
  /**
   * 用户微信unionId
   */
  unionId?: string;
};

export class BaseUser implements IBaseUser {
  /**
   * 用户id
   */
  id!: number;
  /**
   * 用户手机号码
   */
  phone!: string;
  /**
   * 用户状态
   */
  status: UserStatus = UserStatus.INACTIVE;
  /**
   * 用户昵称(默认为微信昵称)
   */
  nickname?: string;
  /**
   * 纠错试用次数
   */
  correct_test_count: number = 0;
  /**
   * 智能创作试用次数
   */
  ai_copywriting_test_count: number = 0;

  constructor(user: IBaseUser) {
    Object.assign(this, user);
  }
}

/**
 * @see IUserInfo
 */
export class UserInfo extends BaseUser implements IUserInfo {
  /**
   * 微信信息id
   */
  wx_user_id?: number;
  /**
   * 邀请人用户id
   */
  invitor_id?: number;
  /**
   * API接口访问Token
   */
  access_token!: string;
  /**
   * 分销收益（邀请的用户购买支付套餐时当前用户得到的收益百分比）
   */
  profit_percent!: number;
  /**
   * 剩余纠错或智能创作次数，值为{@link COUNT_NO_LIMIT}时表示无限
   */
  correct_count!: number;
  /**
   * 已邀请朋友数
   */
  invitees_count!: number;
  /**
   * 用户微信信息
   */
  wechatInfo?: IWechatInfo;
  /**
   * 用户钱包信息
   */
  wallet!: IUserWallet;

  constructor(user: IUserInfo) {
    super(user);
    Object.assign(this, user);
  }
}

/**
 * @see IUser
 */
export class User extends BaseUser implements IUser {
  /**
   * 用户邀请码
   */
  inviteCode!: string;
  /**
   * 用户填写的邀请码
   */
  invitedCode?: string;
  /**
   * 访问Token
   */
  accessToken!: string;
  /**
   * 剩余纠错次数，-101表示无限
   */
  correctCount: number = 0;
  /**
   * 用户头像，最后一个数值代表正方形头像
   * 大小（有 0、46、64、96、132 数值可选，0 代表 640*640 正方形头像），用
   * 户没有头像时该项为空，默认为132
   * 如: https://thirdwx.qlogo.cn/mmopen/vi_32/Q0j4TwGTfTJBldWDdFMnRX0yXMdBSPeThAuTrTZwoogs77AGu4HX9iaiaqzn3bQWHuuw28elL0PGqpricEFeHibsAw/132
   */
  avatar?: string;
  /**
   * 用户微信sessionKey
   */
  sessionKey?: string;
  /**
   * 用户微信unionId
   */
  unionId?: string;

  constructor(user: IUser) {
    super(user);
    Object.assign(this, user);
  }
}

/**
 * 从{@link IUserInfo}转成{@link IUser}的结果
 */
export type IUserFormInfo = IUser & {
  /**
   * 用户填写的邀请码，新版本用户信息没有返回此字段
   * @deprecated
   */
  inviteCode: "";
  /**
   * 用户微信sessionKey，新版本用户信息没有返回此字段
   * @deprecated
   */
  sessionKey?: undefined;
  /**
   * 用户微信unionId，新版本用户信息没有返回此字段
   * @deprecated
   */
  unionId?: undefined;
};

/**
 * 把{@link IUserInfo}转成{@link IUser}，
 * 注意，转换结果`inviteCode`、`sessionKey`和`unionId`字段将为空或undefined
 * @see IUserFormInfo
 */
export function getUserFrom(info: IUserInfo): IUserFormInfo {
  // 公共
  const {
    id,
    phone,
    status,
    nickname,
    correct_test_count,
    ai_copywriting_test_count,
  } = info;
  return {
    id,
    phone,
    status,
    nickname,
    correct_test_count,
    ai_copywriting_test_count,
    inviteCode: "",
    accessToken: info.access_token,
    correctCount: info.correct_count,
    avatar: info.wechatInfo?.headimgurl,
  };
}

/**
 * 从{@link IUser}转成{@link IUserInfo}的结果，
 */
export type InfoFromUser = IUserInfo & {
  /**
   * 微信信息id，旧版本用户信息没有微信信息id
   * @deprecated
   */
  wx_user_id?: undefined;
  /**
   * 邀请人用户id，旧版本用户信息没有邀请人id
   * @deprecated
   */
  invitor_id?: undefined;
  /**
   * 分销收益（邀请的用户购买支付套餐时当前用户得到的收益百分比），
   * 旧版本用户信息没有分销信息
   * @deprecated
   */
  profit_percent: 0;
  /**
   * 已邀请朋友数，旧版本用户信息没有此字段
   * @deprecated
   */
  invitees_count: 0;
  /**
   * 用户微信信息
   */
  wechatInfo: IWechatInfo & {
    /**
     * 已邀请朋友数，旧版本用户信息没有此字段
     * @deprecated
     */
    id: 0;
    /**
     * 已邀请朋友数，旧版本用户信息没有此字段
     * @deprecated
     */
    nickname: "";
  };
  /**
   * 用户钱包信息，旧版本用户信息没有此字段，
   * 此对象的所有值都无效
   * @deprecated
   */
  wallet: IUserWallet & {
    id: 0;
    user_id: 0;
    total_amount: 0;
    withdrawed: 0;
    updated_at: 0;
    created_at: 0;
    coming_amount: 0;
  };
};

/**
 * 把{@link IUser}转成{@link IUserInfo}
 * 注意，转换结果的{@link InfoFromUser 很多字段会作废}
 */
export function getInfoFrom(user: IUser): InfoFromUser {
  // 公共
  const {
    id,
    phone,
    status,
    nickname,
    correct_test_count,
    ai_copywriting_test_count,
  } = user;
  return {
    id,
    phone,
    status,
    nickname,
    correct_test_count,
    ai_copywriting_test_count,
    access_token: user.accessToken,
    profit_percent: 0,
    correct_count: user.correctCount,
    invitees_count: 0,
    wechatInfo: {
      id: 0,
      headimgurl: user.avatar,
      nickname: "",
    },
    wallet: {
      id: 0,
      user_id: 0,
      total_amount: 0,
      withdrawed: 0,
      updated_at: 0,
      created_at: 0,
      coming_amount: 0,
    },
  };
}
